import { formatearCLP, formatearPorcentajeSimple } from '@lexicore/shared';
import { BarraAvance, BarraPresupuesto, Panel, Vacio } from '../../componentes/UI';
import type { ProyectoDetalle } from '../../lib/consultas';

/** Plan contra real y proyección de sobrecosto: cómo va el proyecto en plata. */
export function PestanaResumen({ proyecto }: { proyecto: ProyectoDetalle }) {
  const { analisisCostos, sobrecosto } = proyecto;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 16 }}>
      <Panel titulo="Plan contra real por categoría" subtitulo="Ordenado por desviación: lo que más se escapó, primero">
        {analisisCostos.porCategoria.length === 0 ? (
          <Vacio
            titulo="Sin presupuesto ni gastos"
            detalle="Define el presupuesto en su pestaña y registra gastos para ver dónde se desvía. Con solo el total no se puede saber qué categoría se pasó."
          />
        ) : (
          <>
            <div style={{ display: 'flex', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
              {[
                { etiqueta: 'Planificado', valor: analisisCostos.totalPlanificado, color: 'var(--text-muted)' },
                { etiqueta: 'Real', valor: analisisCostos.totalReal, color: 'var(--text-primary)' },
                {
                  etiqueta: 'Desviación',
                  valor: analisisCostos.desviacion,
                  color: analisisCostos.desviacion > 0 ? 'var(--accent-red)' : 'var(--accent-green)',
                },
              ].map((celda) => (
                <div
                  key={celda.etiqueta}
                  style={{
                    background: 'var(--bg-elevated)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: 'var(--radius-sm)',
                    padding: '10px 14px',
                    flex: 1,
                    minWidth: 130,
                  }}
                >
                  <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>{celda.etiqueta}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 15, fontWeight: 700, marginTop: 3, color: celda.color }}>
                    {celda.etiqueta === 'Desviación' && celda.valor > 0 ? '+' : ''}
                    {formatearCLP(celda.valor)}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {analisisCostos.porCategoria.map((linea) => (
                <div key={linea.categoriaGastoId} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-primary)' }}>
                      {linea.categoriaNombre}
                      {linea.fueraDePresupuesto && (
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            padding: '2px 7px',
                            borderRadius: 20,
                            background: 'var(--accent-red-soft)',
                            color: 'var(--accent-red)',
                            textTransform: 'uppercase',
                          }}
                        >
                          sin presupuestar
                        </span>
                      )}
                    </span>
                    <div style={{ display: 'flex', gap: 14, fontFamily: 'var(--font-mono)', fontSize: 11.5, whiteSpace: 'nowrap' }}>
                      <span style={{ color: 'var(--text-muted)' }}>{formatearCLP(linea.planificado)}</span>
                      <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{formatearCLP(linea.real)}</span>
                      <span
                        style={{
                          color: linea.desviacion > 0 ? 'var(--accent-red)' : 'var(--accent-green)',
                          fontWeight: 700,
                          minWidth: 92,
                          textAlign: 'right',
                        }}
                      >
                        {linea.desviacion > 0 ? '+' : ''}
                        {formatearCLP(linea.desviacion)}
                      </span>
                    </div>
                  </div>
                  <BarraPresupuesto porcentaje={linea.consumoPct ?? (linea.real > 0 ? 100 : 0)} />
                </div>
              ))}
            </div>
          </>
        )}
      </Panel>

      <Panel titulo="Proyección" subtitulo="Extrapola el ritmo de gasto para avisar a tiempo">
        {sobrecosto === null || sobrecosto.costoFinalEstimado === null ? (
          <Vacio
            titulo="Todavía es pronto para proyectar"
            detalle="Con menos del 10% del plazo transcurrido, extrapolar el gasto daría un número sin fundamento. La proyección aparece cuando el proyecto avanza."
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
                <span>Avance del plazo</span>
                <span style={{ fontFamily: 'var(--font-mono)' }}>{formatearPorcentajeSimple(sobrecosto.avanceTemporal * 100)}</span>
              </div>
              <BarraAvance porcentaje={sobrecosto.avanceTemporal * 100} />
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
                <span>Presupuesto consumido</span>
                <span style={{ fontFamily: 'var(--font-mono)' }}>{formatearPorcentajeSimple(sobrecosto.avanceGasto * 100)}</span>
              </div>
              <BarraPresupuesto porcentaje={sobrecosto.avanceGasto * 100} />
            </div>

            <div
              style={{
                padding: '14px 16px',
                borderRadius: 'var(--radius-sm)',
                fontSize: 12.5,
                lineHeight: 1.55,
                background: sobrecosto.enRiesgo ? 'var(--accent-red-soft)' : 'var(--accent-green-soft)',
                color: sobrecosto.enRiesgo ? 'var(--accent-red)' : 'var(--accent-green)',
              }}
            >
              {sobrecosto.enRiesgo ? (
                <>
                  Al ritmo actual terminaría costando{' '}
                  <strong style={{ fontFamily: 'var(--font-mono)' }}>{formatearCLP(sobrecosto.costoFinalEstimado)}</strong>, unos{' '}
                  <strong style={{ fontFamily: 'var(--font-mono)' }}>{formatearCLP(sobrecosto.sobrecostoEstimado ?? 0)}</strong> sobre lo
                  presupuestado.
                </>
              ) : (
                <>
                  Al ritmo actual cerraría en{' '}
                  <strong style={{ fontFamily: 'var(--font-mono)' }}>{formatearCLP(sobrecosto.costoFinalEstimado)}</strong>, dentro de lo
                  presupuestado.
                </>
              )}
              <div style={{ fontSize: 10.5, marginTop: 8, opacity: 0.85 }}>
                Es una proyección lineal: supone que el gasto se reparte parejo en el tiempo.
              </div>
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
}
